// File: src/components/legal/userLegalText.js

export const USER_TERMS_OF_SERVICE = `TRIBE TERMS OF SERVICE (USERS)
Effective Date: September 8, 2026

IMPORTANT: THIS AGREEMENT CONTAINS AN ARBITRATION AGREEMENT AND CLASS ACTION WAIVER THAT AFFECT YOUR RIGHTS.
PLEASE READ CAREFULLY.

These Terms of Service (“Terms”) govern your access to and use of the Tribe platform, website, and related services (“Tribe,” “we,” “us,” “our,” or the “Services”). By accessing or using the Services, you agree to be legally bound by these Terms and all policies incorporated by reference, including our Privacy Policy and Community Guidelines.

If you do not agree, do not use the Services.

1. ELIGIBILITY

You must be at least 18 years old to use Tribe. By using the Services, you represent and warrant that you are 18 or older and that the information you provide is accurate and current.

Certain gatherings may have additional eligibility requirements, including age or other participation requirements. For example, some gatherings may be limited to members who are 21 or older. Any additional eligibility requirements will be disclosed in the applicable gathering details.

Tribe may operate on an invite-only basis. Account registration may require a valid invitation or invitation code. Tribe may limit, revoke, expire, or otherwise manage invitations and access to the Services at its discretion.

2. YOUR ACCOUNT

You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.

You may not:
- share, sell, or transfer your Tribe account;
- create fraudulent, duplicate, or misleading accounts;
- impersonate another person;
- provide false eligibility, age, identity, or profile information;
- misuse invitation or referral systems.

You must notify us if you believe your account has been accessed without authorization.

We may require verification steps where reasonably necessary, including phone, email, identity, age, device, location, or other eligibility checks. We may temporarily restrict access while a verification or safety review is pending.

3. WHAT TRIBE IS

Tribe is a social platform designed to help people meet new people and discover their city through small, hosted in-person gatherings.

Members can browse available gatherings, reserve spots, attend hosted experiences, communicate with their gathering group through Tribe features such as event chats or portals, and reconnect with people they meet through the Services.

Gatherings may include different activities, formats, venues, neighborhoods, hosts, eligibility requirements, and group sizes.

Tribe may provide personalized gathering recommendations based on information such as your preferences, eligibility, activity on Tribe, prior reservations or attendance, and available gatherings.

TRIBE DOES NOT:
- guarantee that you will make friends, matches, business contacts, or romantic connections;
- guarantee compatibility, chemistry, outcomes, attendance, or enjoyment;
- guarantee that any particular member, host, venue, or attendee will attend;
- supervise or monitor all in-person interactions in real time;
- verify every statement made by users;
- conduct background checks on users unless expressly stated for a particular feature or workflow;
- provide transportation, security, medical services, or emergency services.

Hosts are intended to help facilitate gatherings, welcome attendees, provide meeting-point guidance, assist with group coordination, and record attendance where applicable. Hosts are not responsible for guaranteeing your safety or controlling the conduct of other attendees, venues, or third parties.

4. ASSUMPTION OF RISK; OFFLINE RESPONSIBILITY

YOU UNDERSTAND THAT TRIBE FACILITATES REAL-WORLD, IN-PERSON INTERACTIONS WITH OTHER PEOPLE.

YOU ASSUME THE RISKS ARISING FROM OR RELATING TO:
- meeting people you do not already know;
- attending gatherings, events, venues, or other locations;
- travel to and from gatherings;
- activities conducted during gatherings;
- food, beverages, alcohol, exercise, outdoor activities, transportation, or third-party services;
- user, host, venue, or third-party conduct;
- harassment, misconduct, theft, property damage, illness, injury, or other real-world risks.

You are responsible for your own decisions, conduct, transportation, belongings, health, and personal safety.

Use good judgment, respect personal boundaries, follow applicable laws and venue rules, and leave any situation that feels unsafe.

If you are experiencing an emergency, contact the appropriate emergency services.

5. USER CONDUCT; PROHIBITED ACTIVITIES

You agree not to:
- harass, stalk, threaten, intimidate, abuse, harm, or discriminate against others;
- engage in unlawful, exploitative, coercive, or sexually inappropriate conduct;
- impersonate another person or misrepresent your identity;
- provide knowingly false profile, age, reservation, attendance, or eligibility information;
- collect, scrape, harvest, sell, or misuse information about Tribe users;
- spam other users or use Tribe for unauthorized solicitation;
- promote scams, fraud, phishing, malware, or deceptive activity;
- misuse Tribe invitations, referral systems, reservations, attendance systems, chats, or gathering portals;
- intentionally reserve gatherings you do not plan to attend;
- interfere with host check-in or attendance records;
- circumvent reservation limits, eligibility restrictions, suspensions, or other platform controls;
- reverse engineer, interfere with, damage, copy, or misuse the Services;
- violate applicable laws, regulations, venue rules, or Tribe policies.

We may investigate suspected violations and take action where appropriate, including removing content, limiting features, releasing reservations, restricting reservation access, issuing attendance warnings, suspending accounts, or permanently removing users from Tribe.

6. CONTENT; LICENSE

You retain ownership of content you submit to Tribe (“User Content”).

You grant Tribe a worldwide, non-exclusive, royalty-free, sublicensable and transferable license to host, store, reproduce, display, distribute, format, and otherwise use your User Content as reasonably necessary to:
- operate the Services;
- display your content to other users where a Tribe feature calls for it;
- facilitate gatherings and communications;
- maintain, improve, and protect the Services;
- promote Tribe where you have otherwise authorized or made content available for that purpose;
- comply with law.

You represent and warrant that you have the rights necessary to submit your User Content and that it does not violate applicable law or third-party rights.

We may remove or restrict access to content that violates these Terms, our policies, applicable law, or the rights or safety of others.

7. COMMUNICATIONS; SMS; NOTIFICATIONS

Tribe may communicate with you through the Services, email, browser or PWA notifications, and other communication channels used to operate and support Tribe.

TRIBE SMS PROGRAM

Tribe SMS is optional.

If you affirmatively select “Enable text updates” through Tribe’s SMS consent flow, you consent to receive recurring SMS/text messages from Tribe at the mobile phone number you provide, including messages sent using automated technology.

Tribe SMS messages may include:
- notifications when new Tribe gatherings become available;
- personalized gathering recommendations;
- information about gatherings currently available to you;
- reminders for gatherings you reserve;
- gathering date, time, meeting-point, location, or schedule information;
- gathering changes, delays, cancellations, or other operational updates;
- notifications before a gathering begins;
- notifications when a gathering is starting;
- links to gathering details, chats, or the Tribe gathering portal;
- other service-related Tribe notifications consistent with the SMS program you opted into.

Reservation reminders may be sent at various times before a gathering, including approximately 48 hours, 24 hours, and 2 hours before the gathering, as well as around the gathering start time.

Message frequency varies. Message and data rates may apply.

Reply STOP at any time to unsubscribe from Tribe SMS messages.

Reply HELP for help. For additional assistance, contact support@jointribe.io.

Consent to receive Tribe SMS is optional and is not required to create an account, maintain membership, reserve gatherings, or otherwise use Tribe.

If you select “Not now” during the SMS consent flow, you may continue using Tribe without enrolling in recurring SMS notifications.

If you opt out of SMS, you may continue using Tribe, but you may not receive time-sensitive gathering communications by text.

Carriers are not liable for delayed or undelivered messages.

You may also enable browser or PWA notifications where supported. You can revoke those permissions through your device or browser settings.

8. LOCATION FEATURES

Tribe may use location information to support features including:
- showing gatherings in your area;
- determining geographic eligibility or proximity;
- providing directions;
- supporting gathering check-in or attendance features;
- assisting with location-based event or safety functionality.

Approximate location may be inferred from information such as your IP address.

Precise location is collected only where you grant the applicable permission or otherwise provide it.

You can disable device location permissions, although certain location-dependent features may not function.

9. FREE MEMBERSHIP; RESERVATIONS; ATTENDANCE

Tribe currently offers free membership and free reservations for standard Tribe gatherings.

Unless expressly disclosed for a particular offering:
- there is no recurring membership subscription fee;
- there is no fee simply to reserve a standard Tribe gathering;
- Tribe does not require a payment card simply to maintain standard membership or reserve a standard gathering.

You remain responsible for your own purchases and expenses associated with attending a gathering, including food, beverages, transportation, admission, merchandise, or other purchases from venues or third parties unless Tribe expressly states otherwise.

RESERVATION ALLOWANCE

Eligible members may currently hold or make up to three Tribe gathering reservations per applicable refill period.

Your available reservation allowance and refill date may be displayed within Tribe.

A reservation uses available reservation capacity. Reservation access does not guarantee that every gathering will remain available.

Gatherings may become unavailable because of capacity, eligibility requirements, cancellation, scheduling changes, or other operational reasons.

Tribe may adjust reservation limits, refill mechanics, or reservation eligibility as the Services evolve. Current limits displayed in the Services control if they differ from these Terms.

CANCELLATIONS

If you can no longer attend a gathering, you should cancel your reservation as early as reasonably possible so another member may attend.

A cancellation made within 24 hours of the gathering start time may be treated as a late cancellation.

Tribe currently does not charge monetary late-cancellation or no-show fees for standard membership gatherings.

ATTENDANCE AND NO-SHOWS

Tribe records attendance to help manage reservation access and encourage members to use reservations responsibly.

If you reserve a gathering and do not attend without properly canceling, Tribe may record a no-show.

Under Tribe’s current attendance policy:
- one no-show may place your reservation access into an Attendance Warning status;
- while in Attendance Warning status, attending two reserved gatherings in a row restores your attendance standing;
- only attended gatherings advance the attendance recovery requirement;
- a second no-show may result in suspension of reservation access.

The applicable attendance status and recovery progress may be displayed within Tribe.

Repeated misuse of reservations, repeated no-shows, fraudulent attendance activity, or attempts to circumvent attendance controls may result in additional restrictions or removal from Tribe.

Tribe may correct attendance records where appropriate, including where a host or system failed to record an attendee accurately.

10. GATHERINGS; HOSTS; LOCATIONS

Gatherings are subject to availability, capacity, eligibility, venue availability, weather, host availability, operational considerations, and other factors.

Tribe may change, postpone, relocate, or cancel a gathering where reasonably necessary.

We may communicate important gathering changes through the Tribe platform, SMS where you have opted in, email, event chat, or other available communication channels.

Certain gathering information may be intentionally limited before reservation.

For example, Tribe may show a neighborhood or general area before reservation while making the full meeting address available only after a member reserves.

Some gatherings may be limited to particular ages or other eligibility groups. If a gathering is designated 21+, you must be at least 21 years old to reserve or participate.

You are responsible for complying with applicable laws relating to alcohol and for making your own decisions regarding alcohol consumption.

Hosts may:
- welcome and introduce attendees;
- help the group begin the gathering;
- provide meeting-point or route information;
- use Tribe communication tools to coordinate the group;
- check attendees in;
- record attendance;
- communicate operational information.

A host's presence does not make Tribe responsible for individual attendee conduct or eliminate the ordinary risks associated with meeting people or attending real-world activities.

11. THIRD-PARTY SERVICES AND VENUES

The Services may link to, integrate with, or rely on third-party products and services, including:
- venues;
- restaurants, bars, museums, shops, fitness providers, and activity operators;
- mapping and navigation services;
- communications and messaging providers;
- analytics providers;
- infrastructure and hosting providers;
- customer support, security, fraud-prevention, and verification providers.

Tribe does not control third-party products, services, venues, availability, pricing, conduct, safety practices, or policies.

Your use of third-party services may be subject to separate third-party terms and policies.

12. DISCLAIMERS

TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE.”

TRIBE DISCLAIMS WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AVAILABILITY, ACCURACY, AND FITNESS FOR ANY PARTICULAR SOCIAL OR PERSONAL OUTCOME.

TRIBE DOES NOT WARRANT THAT:
- THE SERVICES WILL ALWAYS BE AVAILABLE, SECURE, OR ERROR-FREE;
- A PARTICULAR GATHERING WILL OCCUR;
- A PARTICULAR PERSON WILL ATTEND;
- YOU WILL FORM A FRIENDSHIP, RELATIONSHIP, MATCH, OR OTHER CONNECTION;
- OTHER USERS WILL BEHAVE APPROPRIATELY OR LAWFULLY;
- INFORMATION PROVIDED BY USERS, HOSTS, VENUES, OR THIRD PARTIES WILL ALWAYS BE ACCURATE.

13. LIMITATION OF LIABILITY

TO THE MAXIMUM EXTENT PERMITTED BY LAW:

- TRIBE WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES;
- TRIBE WILL NOT BE LIABLE FOR LOST PROFITS, LOST DATA, LOSS OF GOODWILL, OR LOST OPPORTUNITIES;
- TRIBE WILL NOT BE LIABLE FOR USER CONDUCT OR FOR OFFLINE INTERACTIONS BETWEEN USERS;
- TRIBE WILL NOT BE LIABLE FOR THE ACTS OR OMISSIONS OF VENUES OR THIRD-PARTY SERVICE PROVIDERS;
- TRIBE WILL NOT BE LIABLE FOR INJURY, LOSS, OR DAMAGE ARISING FROM RISKS YOU ASSUME UNDER THESE TERMS EXCEPT TO THE EXTENT LIABILITY CANNOT LEGALLY BE LIMITED.

TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRIBE’S TOTAL LIABILITY FOR CLAIMS ARISING OUT OF OR RELATING TO THE SERVICES WILL NOT EXCEED THE GREATER OF (A) $100 OR (B) THE AMOUNT YOU PAID DIRECTLY TO TRIBE DURING THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM.

Some jurisdictions do not allow certain limitations of liability. In those jurisdictions, these limitations apply only to the maximum extent permitted by law.

14. INDEMNIFICATION

To the extent permitted by law, you agree to defend, indemnify, and hold harmless Tribe and its operators, affiliates, employees, contractors, hosts, representatives, and agents from claims, damages, losses, liabilities, costs, and expenses, including reasonable attorneys’ fees, arising from or related to:
- your use of the Services;
- your User Content;
- your interactions with other users;
- your attendance at or conduct during a gathering;
- your violation of these Terms, Tribe policies, venue rules, or applicable law;
- your infringement of another person’s rights.

15. SUSPENSION; RESTRICTION; TERMINATION

Tribe may restrict, suspend, or terminate access to the Services where reasonably appropriate, including for:
- violations of these Terms or Community Guidelines;
- safety concerns;
- harassment or misconduct;
- fraud or deception;
- account or invitation abuse;
- repeated no-shows;
- misuse of reservations or attendance systems;
- spam or unauthorized solicitation;
- attempts to evade a prior restriction or suspension;
- risks to Tribe, its members, hosts, venues, or the community.

Depending on the circumstances, Tribe may:
- issue an attendance warning;
- restrict reservation access;
- release future reservations;
- restrict messaging or chat features;
- suspend an account;
- remove an account from Tribe;
- prohibit participation in future gatherings.

Nothing in these Terms requires Tribe to continue offering the Services or any particular feature.

16. DISPUTE RESOLUTION; ARBITRATION; CLASS ACTION WAIVER

PLEASE READ THIS SECTION CAREFULLY.

A. INFORMAL RESOLUTION

Before initiating arbitration or another formal legal proceeding, you agree to contact Tribe at support@jointribe.io and attempt in good faith to resolve the dispute informally for at least 30 days.

B. INDIVIDUAL ARBITRATION

Except for claims that may properly be brought in small claims court and claims for which applicable law prohibits arbitration, disputes arising out of or relating to these Terms or the Services will be resolved through binding individual arbitration under the Federal Arbitration Act.

Arbitration will be conducted on an individual basis before a single neutral arbitrator.

C. CLASS AND REPRESENTATIVE ACTION WAIVER

TO THE EXTENT PERMITTED BY LAW, YOU AND TRIBE AGREE THAT CLAIMS WILL BE BROUGHT ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF, CLASS MEMBER, OR REPRESENTATIVE IN A CLASS, COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING.

D. ARBITRATION PROCEDURE

The arbitration will take place in the United States, in English, before a single arbitrator.

The arbitrator may award relief available under applicable law, but only with respect to the individual claims before the arbitrator.

If a court determines that a portion of this arbitration section is unenforceable for a particular claim or remedy, the remaining portions will continue to apply to the maximum extent permitted by law.

17. GOVERNING LAW

To the extent not preempted by federal law, these Terms are governed by the laws of the State of California, without regard to conflict-of-law principles.

18. CHANGES TO THESE TERMS

We may update these Terms as the Services evolve.

When we make material changes, we may provide notice through the Services or another appropriate communication method.

Updated Terms will be posted with a revised effective date.

Where applicable law requires affirmative consent to a change, continued use alone will not substitute for that required consent.

19. MISCELLANEOUS

- Severability: If any provision is determined to be unenforceable, the remaining provisions remain in effect.
- No Waiver: Failure to enforce a provision does not waive the right to enforce it later.
- Assignment: Tribe may assign these Terms in connection with a restructuring, financing, acquisition, sale, or transfer of the Services. You may not assign your rights under these Terms without our consent.
- Entire Agreement: These Terms, our Privacy Policy, Community Guidelines, and any additional terms expressly presented for a particular feature constitute the agreement between you and Tribe regarding the Services.
- Contact: Questions about these Terms may be sent to support@jointribe.io.
`;


export const USER_PRIVACY_POLICY = `TRIBE PRIVACY POLICY (USERS)
Effective Date: September 8, 2026

This Privacy Policy explains how Tribe (“Tribe,” “we,” “us,” or “our”) collects, uses, discloses, and retains information from individuals who access or use the Tribe platform, website, gatherings, and related services (“Users,” “you”).

By using the Services, you acknowledge the practices described in this Privacy Policy.

1. INFORMATION WE COLLECT

We may collect, store, and process the following categories of information.

1.1 INFORMATION YOU PROVIDE

We may collect information you provide directly, including:
- name;
- email address;
- mobile phone number;
- date of birth and age information used for eligibility;
- profile information, including photos, bio, interests, preferences, and related account details;
- invitation, invite-code, and referral information;
- SMS consent status, SMS consent records, and messaging preferences;
- messages, chats, feedback, reports, and communications with other users or with Tribe;
- gathering reservations;
- gathering attendance;
- cancellations and late cancellations;
- no-show status;
- check-in information;
- support requests;
- safety reports;
- other information you choose to provide through the Services.

1.2 USAGE, DEVICE, AND LOG DATA

We may automatically collect information about your use of Tribe, including:
- IP address;
- device identifiers;
- browser type;
- operating system;
- timestamps;
- app and site interactions;
- pages and features viewed;
- gathering views and interactions;
- reservation activity;
- notification interactions;
- chat and portal activity;
- diagnostic information;
- security and fraud-prevention signals;
- approximate location inferred from IP address or similar information.

1.3 LOCATION DATA

Where you grant permission or otherwise provide location information, Tribe may collect precise or more specific location information.

Location information may be used to:
- show gatherings in your area;
- determine geographic eligibility or proximity;
- provide directions;
- support gathering check-in or attendance features;
- power location-dependent product features;
- support safety, fraud-prevention, or operational functionality.

You can disable device location permissions, although certain location-dependent features may not function.

1.4 INFORMATION ABOUT GATHERING ACTIVITY

We may collect and maintain information relating to your participation in Tribe gatherings, including:
- gatherings viewed;
- recommendations shown to you;
- reservations;
- reservation limits and refill status;
- cancellations;
- late cancellations;
- attendance;
- no-shows;
- attendance-warning status;
- attendance recovery progress;
- check-in information;
- event chat or portal participation;
- gathering feedback and reports.

1.5 INFORMATION FROM THIRD PARTIES

We may receive information from third parties that help us operate Tribe, including providers of:
- hosting and infrastructure;
- analytics;
- messaging and communications;
- mapping and location services;
- security and fraud prevention;
- customer support;
- identity, eligibility, or safety verification;
- other technology or operational services.

We may also receive information from law enforcement, regulators, venues, hosts, or other users where appropriate for safety, legal, support, or operational purposes.

2. HOW WE USE INFORMATION

We may use information for purposes including:
- creating and managing Tribe accounts;
- administering invite-only registration;
- verifying eligibility;
- operating, maintaining, and improving the Services;
- showing available Tribe gatherings;
- personalizing gathering recommendations;
- determining gathering eligibility;
- managing reservations and reservation limits;
- displaying reservation refill information;
- recording cancellations, late cancellations, attendance, and no-shows;
- administering attendance warnings and reservation-access status;
- supporting host check-in;
- operating gathering chats and portals;
- helping users locate or coordinate with their gathering group;
- sending service communications;
- sending SMS messages where you have opted in;
- sending browser or PWA notifications where permitted;
- providing customer support;
- investigating safety concerns or misconduct;
- preventing fraud, spam, abuse, or unauthorized access;
- enforcing our Terms and Community Guidelines;
- conducting analytics, research, testing, debugging, and product optimization;
- complying with legal obligations;
- resolving disputes;
- protecting Tribe, our users, hosts, venues, and others.

We may use aggregated or de-identified information for research, analytics, product development, and other lawful purposes.

2.1 SMS AND MOBILE INFORMATION

Tribe does not enroll you in recurring SMS messages merely because you provide a mobile phone number.

If you affirmatively opt in through Tribe’s SMS consent flow, we may use your mobile phone number and SMS consent information to send recurring Tribe text messages.

These messages may include:
- notifications when new Tribe gatherings become available;
- personalized gathering recommendations;
- information about gatherings available to you;
- reminders for gatherings you reserve;
- time, meeting-point, location, or schedule information;
- gathering changes or cancellations;
- start-time notifications;
- links to gathering details, chats, or the Tribe portal.

Message frequency varies.

Message and data rates may apply.

You may opt out of Tribe SMS at any time by replying STOP.

You may reply HELP for help or contact support@jointribe.io.

NO MOBILE INFORMATION, INCLUDING MOBILE PHONE NUMBERS, SMS OPT-IN DATA, OR SMS CONSENT, WILL BE SHARED WITH THIRD PARTIES OR AFFILIATES FOR THEIR MARKETING OR PROMOTIONAL PURPOSES.

Tribe may disclose mobile information to service providers solely as necessary to provide messaging, communications, security, infrastructure, or related services on Tribe’s behalf.

Those service providers may use the information only to provide services to Tribe and not for their own marketing or promotional purposes.

SMS consent is specific to Tribe and is not sold, rented, transferred, or shared with another business for that business’s marketing purposes.

3. HOW WE DISCLOSE INFORMATION

We may disclose information in the circumstances described below.

3.1 SERVICE PROVIDERS

We may disclose information to vendors and service providers that perform functions on our behalf, including:
- hosting and infrastructure;
- analytics;
- communications and SMS delivery;
- mapping and location services;
- customer support;
- fraud prevention;
- security;
- identity or eligibility verification;
- product and technical operations.

These providers are expected to process information for the services they provide to Tribe and subject to applicable contractual, privacy, and security obligations.

The mobile-information restrictions described in Section 2.1 apply to mobile numbers, SMS opt-in data, and SMS consent.

3.2 OTHER USERS, HOSTS, AND GATHERING PARTICIPANTS

Certain information is shared with other users where necessary for Tribe’s social and gathering features.

Depending on the feature, this may include:
- your profile information;
- profile photo;
- display name;
- messages you send;
- gathering participation;
- chat participation;
- other information you intentionally make visible through Tribe.

Hosts may receive information reasonably necessary to facilitate a gathering, identify attendees, communicate with the group, and record attendance.

3.3 LEGAL, SAFETY, AND SECURITY REASONS

We may disclose information if we reasonably believe disclosure is necessary to:
- comply with applicable law, regulation, subpoena, court order, or legal process;
- respond to lawful government requests;
- investigate fraud, abuse, misconduct, or security incidents;
- enforce our Terms or policies;
- protect the rights, property, or safety of Tribe, users, hosts, venues, or the public;
- respond to an emergency involving risk of harm.

3.4 BUSINESS TRANSFERS

If Tribe is involved in a financing, merger, acquisition, reorganization, sale of assets, or similar business transaction, information may be transferred as part of that transaction subject to applicable law.

SMS opt-in consent does not automatically authorize a new or unrelated business to use your mobile information for its own marketing or promotional messages.

4. DATA RETENTION

We retain information for as long as reasonably necessary for purposes including:
- operating the Services;
- maintaining accounts;
- administering reservations and attendance;
- preserving safety and trust records;
- preventing fraud and abuse;
- responding to support requests;
- enforcing policies;
- complying with legal obligations;
- resolving disputes;
- maintaining legitimate business records.

Retention periods may vary based on the type of information and why we collected it.

We may retain certain information after account deletion where permitted or required by law, including information necessary for safety, fraud prevention, dispute resolution, or enforcement.

5. SECURITY

We use reasonable administrative, technical, and organizational safeguards designed to protect information.

However, no internet, mobile, storage, or communications system is completely secure, and we cannot guarantee absolute security.

You are responsible for protecting access to your own account and devices.

6. YOUR RIGHTS AND CHOICES

Depending on where you live, applicable law may give you rights relating to your personal information, which may include rights to:
- access certain personal information;
- request correction;
- request deletion;
- receive information about certain disclosures;
- object to or limit certain processing;
- exercise other rights provided by applicable law.

Requests may be submitted to:

privacy@jointribe.io

We may need to verify your identity before completing certain requests.

You can also:
- decline Tribe SMS enrollment during registration;
- opt out of SMS at any time by replying STOP;
- manage browser or PWA notification permissions through your device or browser;
- manage location permissions through your device;
- cancel gathering reservations through available Tribe functionality.

7. NO SALE OF PERSONAL INFORMATION

Tribe does not sell personal information as defined by applicable law.

Where applicable privacy law treats certain forms of advertising-related disclosure as “sharing,” Tribe will provide any rights or choices required by applicable law.

Regardless of other provisions of this Privacy Policy, mobile phone numbers, SMS opt-in data, and SMS consent are not sold or shared with third parties or affiliates for their marketing or promotional purposes.

8. AGE REQUIREMENTS

Tribe is intended only for individuals who are at least 18 years old.

We do not knowingly permit individuals under 18 to create Tribe accounts.

Date-of-birth information may also be used to determine eligibility for gatherings with additional age requirements, including gatherings limited to members who are 21 or older.

9. CHANGES TO THIS PRIVACY POLICY

We may update this Privacy Policy as Tribe evolves or as legal, regulatory, operational, or product requirements change.

Updated versions will be posted with a revised effective date.

Where required by law, we may provide additional notice or request additional consent.

10. CONTACT

Privacy questions or requests:
privacy@jointribe.io

General support:
support@jointribe.io
`;


export const USER_COMMUNITY_GUIDELINES = `TRIBE COMMUNITY GUIDELINES (USERS)
Effective Date: September 8, 2026

Tribe exists to make it easier for people to meet new people, show up to real-world experiences, and build meaningful community.

These Community Guidelines apply to your use of Tribe, including profiles, gatherings, reservations, chats, gathering portals, and interactions with other members, hosts, venues, and Tribe staff.

1. BE RESPECTFUL

Treat other members, hosts, venue staff, and Tribe staff with respect.

Do not:
- harass;
- threaten;
- stalk;
- bully;
- intimidate;
- discriminate;
- use hate speech;
- deliberately humiliate or target another person.

Respect that different people have different comfort levels, personalities, and boundaries.

2. RESPECT CONSENT AND BOUNDARIES

Respect physical, social, romantic, and personal boundaries.

Unwanted sexual behavior, touching, pressure, coercion, or harassment is prohibited.

Do not pressure someone to:
- drink alcohol;
- share personal information;
- exchange contact information;
- leave a gathering with you;
- participate in an activity;
- continue interacting with you.

Obtain permission before taking or posting identifiable photos or videos of another member where consent would reasonably be expected.

3. KEEP IT LAWFUL AND SAFE

Do not use Tribe to facilitate or promote unlawful activity.

Follow applicable laws and venue rules.

Weapons, violence, threats of violence, or dangerous conduct are prohibited at Tribe gatherings.

If a gathering is designated 21+, do not reserve or attend unless you satisfy the age requirement.

You are responsible for your own decisions concerning alcohol and must comply with applicable age and alcohol laws.

4. BE AUTHENTIC

Use your real account and provide accurate information.

Do not:
- impersonate another person;
- misrepresent your age or eligibility;
- create deceptive or fraudulent profiles;
- create duplicate accounts to bypass restrictions;
- misuse another person’s invitation or account;
- misrepresent your attendance.

5. USE INVITES RESPONSIBLY

Tribe may be invite-only.

Do not:
- sell invitations or invitation codes;
- publicly distribute invitation codes where Tribe has provided them for limited use;
- create fake accounts through invitations;
- use invitation systems for spam, fraud, or manipulation;
- attempt to circumvent invitation limits or access restrictions.

6. RESERVATIONS AND ATTENDANCE

Only reserve a gathering you genuinely intend to attend.

Reservations are limited, and holding a spot you do not intend to use can prevent another member from attending.

If you can no longer attend, cancel as early as possible.

Cancellations made within 24 hours of the gathering start time may be treated as late cancellations.

If you reserve and do not attend without properly canceling, Tribe may record a no-show.

Under Tribe’s current attendance policy:
- one no-show may place your account into Attendance Warning;
- attending two reserved gatherings in a row restores your attendance standing;
- a second no-show may result in suspension of reservation access.

Do not:
- falsify attendance;
- ask a host to mark you present when you were not;
- interfere with check-in;
- manipulate attendance records;
- create another account to evade an attendance restriction;
- repeatedly reserve gatherings you do not intend to attend.

7. RESPECT THE GATHERING

Arrive prepared to participate respectfully in the gathering you reserved.

Follow reasonable host instructions relating to:
- where the group is meeting;
- gathering logistics;
- check-in;
- movement between gathering stops;
- venue requirements;
- group coordination.

Hosts are there to facilitate the experience, not to provide personal concierge, transportation, security, or supervision services.

Do not intentionally disrupt the gathering or interfere with other members’ ability to participate.

8. CHAT AND GATHERING PORTALS

Tribe chats and gathering portals are intended to help members:
- coordinate before a gathering;
- find the group;
- receive updates;
- communicate during the gathering;
- reconnect afterward where applicable.

Do not:
- spam;
- harass;
- pressure;
- threaten;
- send unwanted sexual messages;
- repeatedly contact someone who does not want to engage;
- use chats for scams or unauthorized commercial solicitation;
- share another member’s private information without permission.

Tribe may remove messages or restrict messaging access where appropriate.

9. NO EXPLOITATION OR UNAUTHORIZED SOLICITATION

Do not use Tribe primarily to:
- sell products or services;
- recruit users into unrelated businesses;
- promote scams;
- collect leads;
- harvest contact information;
- solicit money;
- pressure users into financial transactions;
- distribute spam.

Reasonable social interaction between members is permitted, but Tribe is not a lead-generation or solicitation platform.

10. PRIVACY

Respect other members’ privacy.

Do not:
- publish another member’s phone number, address, email, workplace, private messages, or other sensitive information without permission;
- scrape or harvest user information;
- record private conversations without lawful permission;
- use Tribe data to create external lists or databases.

Information available through Tribe should be used only for legitimate Tribe-related social and gathering purposes.

11. REPORTING AND SAFETY

If another member’s behavior violates these Guidelines or creates a safety concern, report the issue through available Tribe support or reporting tools.

You may also contact:

support@jointribe.io

If there is an immediate emergency or risk of serious harm, contact local emergency services first.

12. ENFORCEMENT

Tribe may investigate suspected violations and take action based on the circumstances.

Actions may include:
- warnings;
- content removal;
- message removal;
- reservation release;
- attendance warnings;
- restriction of reservation access;
- restriction of chat or portal access;
- suspension;
- removal from gatherings;
- account termination;
- permanent removal from Tribe.

Tribe may consider conduct that occurs outside the Tribe platform where that conduct creates a reasonable safety, trust, fraud, or community concern relating to Tribe or its members.

We may take immediate action where necessary to protect members, hosts, venues, Tribe, or the integrity of the community.

These Guidelines do not limit Tribe’s rights under the Terms of Service or applicable law.
`;